// Utilitario puro para exportar arrays de objetos a CSV/TSV.
// Soporta keys anidadas con notación punto (ej: 'profile.nombre').
// Soporta `format` function por columna para calcular valores derivados o formato.

/**
 * Lee un valor de un objeto soportando keys anidadas: 'a.b.c'
 * @param {object} obj
 * @param {string} path
 * @returns {*}
 */
function getValue(obj, path) {
  if (obj == null) return ''
  return path.split('.').reduce((acc, k) => acc?.[k], obj)
}

/**
 * Escapa un valor para CSV (quoting si tiene coma/comilla/newline).
 * @param {*} val
 * @returns {string}
 */
function csvEscape(val) {
  if (val == null) return ''
  const s = String(val)
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

/**
 * Escapa un valor para TSV (quita tabs/newlines, no necesita quoting).
 * @param {*} val
 * @returns {string}
 */
function tsvEscape(val) {
  if (val == null) return ''
  return String(val).replace(/\t/g, ' ').replace(/\r?\n/g, ' ')
}

/**
 * Convierte data a string CSV con BOM UTF-8 (para que Excel/Sheets lea acentos).
 * @param {Array<object>} data
 * @param {Array<{key: string, label: string, format?: (val, row) => string}>} columns
 * @returns {string}
 */
export function toCsv(data, columns) {
  const header = columns.map(c => csvEscape(c.label)).join(',')
  const rows = (data || []).map(row =>
    columns.map(c => {
      const val = getValue(row, c.key)
      const out = c.format ? c.format(val, row) : val
      return csvEscape(out)
    }).join(',')
  )
  return '﻿' + [header, ...rows].join('\n')  // BOM crítico para acentos
}

/**
 * Convierte data a string TSV (para pegar directo en Google Sheets / Excel).
 * @param {Array<object>} data
 * @param {Array<{key: string, label: string, format?: (val, row) => string}>} columns
 * @returns {string}
 */
export function toTsv(data, columns) {
  const header = columns.map(c => tsvEscape(c.label)).join('\t')
  const rows = (data || []).map(row =>
    columns.map(c => {
      const val = getValue(row, c.key)
      const out = c.format ? c.format(val, row) : val
      return tsvEscape(out)
    }).join('\t')
  )
  return [header, ...rows].join('\n')
}

/**
 * Descarga el contenido como archivo CSV.
 * @param {string} csvContent
 * @param {string} filename — sin extensión
 */
export function downloadCsv(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}.csv`
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 200)
}

/**
 * Copia el contenido al portapapeles. Devuelve Promise<boolean>.
 * @param {string} text
 * @returns {Promise<boolean>}
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Fallback para browsers viejos
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.top = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    try {
      document.execCommand('copy')
      document.body.removeChild(ta)
      return true
    } catch {
      document.body.removeChild(ta)
      return false
    }
  }
}

// Helpers de formato comunes
export const fmt = {
  uyu: (val) => val == null ? '' : Number(val).toFixed(2).replace('.', ','),
  pct: (val) => val == null ? '' : (Number(val) * 100).toFixed(1).replace('.', ',') + '%',
  pctRaw: (val) => val == null ? '' : Number(val).toFixed(1).replace('.', ',') + '%',
  date: (val) => val ? new Date(val).toISOString().slice(0, 10) : '',
  bool: (val) => val === true ? 'Sí' : val === false ? 'No' : '',
  int: (val) => val == null ? '' : Math.round(Number(val)).toString(),
}
